class GalleryEnquiryPopup extends HTMLElement {
  constructor() {
    super();
    /** @type {HTMLFormElement|null} */
    this.form = null;
    /** @type {HTMLButtonElement|null} */
    this.submitBtn = null;
    this.scrollPosition = 0;
    this.messages = {
      /** @param {string} label */
      required: (label) => `${label} is required`,
      consent: () => 'You must accept the privacy policy',
    };
  }

  connectedCallback() {
    this.form = this.querySelector('.gallery-enquiry-form');
    if (!this.form) return;

    this.form.setAttribute('novalidate', '');
    this.submitBtn = this.form.querySelector('.gallery-enquiry-form__submit');

    this.bindLiveClearing();
    this.bindSubmit();
    this.bindDialogClose();
  }

  bindDialogClose() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    const closeDialog = () => {
      dialog.classList.add('is-closing');
      setTimeout(() => {
        dialog.close();
        dialog.classList.remove('is-closing');
      }, 300);
    };

    dialog.addEventListener('click', (e) => {
      const rect = dialog.getBoundingClientRect();
      const isInDialog =
        rect.top <= e.clientY &&
        e.clientY <= rect.top + rect.height &&
        rect.left <= e.clientX &&
        e.clientX <= rect.left + rect.width;

      if (!isInDialog) {
        closeDialog();
      }
    });

    dialog.addEventListener('close', () => {
      document.body.style.overflow = '';
      window.scrollTo(0, this.scrollPosition);
    });

    const observer = new MutationObserver(() => {
      if (dialog.open) {
        this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
        document.body.style.overflow = 'hidden';
      }
    });

    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });

    const closeButton = this.querySelector('.gallery-enquiry__close');
    if (closeButton) {
      closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        closeDialog();
      });
    }
  }

  /**
   * Clear error styling and message for a field
   * @param {HTMLInputElement|HTMLTextAreaElement} field
   */
  clearFieldError(field) {
    field.classList.remove('gallery-enquiry-form__input--error');
    const group =
      field.closest('.gallery-enquiry-form__field-group') || field.closest('.gallery-enquiry-form__consent');
    if (!group) return;
    const errorEl = /** @type {HTMLElement|null} */ (group.querySelector('.gallery-enquiry-form__field-error'));
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.classList.remove('gallery-enquiry-form__field-error--visible');
    }
  }

  /**
   * Show error styling and message for a field
   * @param {HTMLInputElement|HTMLTextAreaElement} field
   * @param {string} message
   */
  showFieldError(field, message) {
    field.classList.add('gallery-enquiry-form__input--error');
    const group =
      field.closest('.gallery-enquiry-form__field-group') || field.closest('.gallery-enquiry-form__consent');
    if (!group) return;
    const errorEl = /** @type {HTMLElement|null} */ (group.querySelector('.gallery-enquiry-form__field-error'));
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.add('gallery-enquiry-form__field-error--visible');
    }
  }

  /**
   * Validate a single field
   * @param {HTMLInputElement|HTMLTextAreaElement} field
   * @returns {string|null}
   */
  validateField(field) {
    const rules = (field.getAttribute('data-validation') || '').split('|').filter(Boolean);
    const label = field.getAttribute('data-label') || 'Field';

    let value;
    if (field.type === 'checkbox') {
      value = field.checked;
    } else {
      value = (field.value || '').trim();
    }

    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];

      if (rule === 'required' && !value) {
        return this.messages.required(label);
      }

      if (rule === 'consent' && !value) {
        return this.messages.consent();
      }
    }

    return null;
  }

  /**
   * Validate all fields in the form
   * @returns {boolean}
   */
  validateForm() {
    if (!this.form) return false;

    const fields = /** @type {NodeListOf<HTMLInputElement|HTMLTextAreaElement>} */ (
      this.form.querySelectorAll('[data-validation]')
    );
    /** @type {HTMLInputElement|HTMLTextAreaElement|null} */
    let firstInvalid = null;
    let isValid = true;

    fields.forEach((field) => {
      this.clearFieldError(field);
      const error = this.validateField(field);
      if (error) {
        isValid = false;
        this.showFieldError(field, error);
        if (!firstInvalid) firstInvalid = field;
      }
    });

    if (firstInvalid) {
      const isCheckbox = firstInvalid.type === 'checkbox';
      const focusTarget = isCheckbox
        ? /** @type {HTMLElement|null} */ (
            /** @type {HTMLElement} */ (firstInvalid).closest('.gallery-enquiry-form__consent')
          )
        : firstInvalid;
      if (focusTarget) focusTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (!isCheckbox) /** @type {HTMLInputElement} */ (firstInvalid).focus();
    }

    return isValid;
  }

  /**
   * Bind live error clearing on input/change
   */
  bindLiveClearing() {
    if (!this.form) return;

    this.form.addEventListener('input', (e) => {
      const field = /** @type {HTMLInputElement|HTMLTextAreaElement} */ (e.target);
      if (field.getAttribute('data-validation')) {
        this.clearFieldError(field);
      }
    });

    this.form.addEventListener('change', (e) => {
      const field = /** @type {HTMLInputElement|HTMLTextAreaElement} */ (e.target);
      if (field.getAttribute('data-validation')) {
        this.clearFieldError(field);
      }
    });
  }

  /**
   * Bind submit validation
   */
  bindSubmit() {
    if (!this.form) return;
    /** @param {Event} e */

    const blockSubmit = (e) => {
      if (!this.validateForm()) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };

    if (this.submitBtn) {
      this.submitBtn.addEventListener('click', blockSubmit, true);
    }

    this.form.addEventListener('submit', blockSubmit, true);
  }
}

customElements.define('gallery-enquiry-popup', GalleryEnquiryPopup);
